import { zodResolver } from "@hookform/resolvers/zod";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { usePdfViewerContext } from "@src/appV2/PdfViewer/context";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { type SignedAtStage } from "../../paths";

const signJobSearchAgreementFormSchema = z.object({
  confirmation: z.boolean().refine((value) => value, {
    message: "Click the checkbox to confirm and proceed.",
  }),
  agreementScrolled: z.boolean().refine((value) => value, {
    message: "You must read the full agreement to proceed.",
  }),
});

export type SignJobSearchAgreementFormValues = z.infer<typeof signJobSearchAgreementFormSchema>;

interface UseJobSearchAgreementFormParams {
  workerId: string;
  agreementVersion?: number;
  signedAtStage: SignedAtStage;
  placementId?: string;
}

export function useJobSearchAgreementForm(params: UseJobSearchAgreementFormParams) {
  const { workerId, agreementVersion, signedAtStage, placementId } = params;
  const hasLoggedScroll = useRef(false);

  const formMethods = useForm({
    defaultValues: {
      confirmation: false,
      agreementScrolled: false,
    },
    mode: "all",
    resolver: zodResolver(signJobSearchAgreementFormSchema),
  });

  const { setValue, trigger } = formMethods;

  const { currentPage, numberOfPages, isSupportedPlatform } = usePdfViewerContext();

  useEffect(() => {
    void trigger();
  }, [trigger]);

  useEffect(() => {
    if (currentPage === numberOfPages || isSupportedPlatform === false) {
      setValue("agreementScrolled", true, { shouldValidate: true });

      //  Log scroll completion event only once
      if (!hasLoggedScroll.current) {
        hasLoggedScroll.current = true;
        logEvent(APP_V2_USER_EVENTS.JOB_SEARCH_AGREEMENT_SCROLLED, {
          workerId,
          agreementVersion,
          signedAtStage,
          placementId,
        });
      }
    }
  }, [
    currentPage,
    numberOfPages,
    isSupportedPlatform,
    setValue,
    workerId,
    agreementVersion,
    signedAtStage,
    placementId,
  ]);

  useLogEffect(APP_V2_APP_EVENTS.PDF_PLATFORM_UNSUPPORTED_JOB_SEARCH_AGREEMENT, undefined, {
    enabled: isSupportedPlatform === false,
  });

  return {
    formMethods,
  };
}
